import classNames from "classnames";
import React, { memo, useCallback, useState } from "react";

import "./ReportingTemplate.scss";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import Badge from "../../../atoms/badge/Badge";
import SortableReportingPagePreviewTile from "./SortableReportingTemplateTile";
import ReportingPagePreviewTile, {
  RENDERED_DIMENSIONS,
} from "./ReportingPagePreviewTile";
import modelApi from "../../../../services/modelApi";
import Banner from "../../../atoms/banner/Banner";
import VerticalGroup from "../../../atoms/verticalgroup/VerticalGroup";
import ContentStretcher from "./ContentStretcher";
import RoundResultsPage from "../../results/RoundResultsPage";

interface Props {
  round: number;
  data: ModelAPI.Reporting.ReportingResults;
  mode: "fullscreen" | "thumbnails";
  setMode: (val: "fullscreen" | "thumbnails") => void;
  previewHeight: number;
  previewWidth: number;
  availableHeight: number;
  availableWidth: number;
  editable?: boolean;
  eventId: string;
}

const SortableReportingTemplatePreviewContent: React.FC<Props> = ({
  eventId,
  data,
  mode,
  round,
  editable,
  availableHeight,
  availableWidth,
  previewHeight,
  previewWidth,
}) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiUpdating, setApiUpdating] = useState(false);
  const [activeLayout, setActiveLayout] =
    useState<API.ReportingTemplateLayoutResponse | null>(null);
  const [slides, setSlides] = useState(data.layouts);

  function handleDragStart(event: any) {
    const { active } = event;

    const slide = slides.find((l) => l.id === active.id);
    if (slide) {
      setActiveLayout(slide);
    }
  }

  const handleDragEnd = useCallback(
    async (event: any) => {
      const { active, over } = event;

      if (active.id !== over.id) {
        const oldIndex = slides.findIndex((l) => l.id === active.id);
        const newIndex = slides.findIndex((l) => l.id === over.id);

        const newSlides = arrayMove(slides, oldIndex, newIndex);
        setSlides(newSlides);
        try {
          setApiUpdating(true);
          await modelApi.updateReportingTemplate(eventId, round, {
            layouts: newSlides.map((l) => ({
              id: l.id,
              overrideDisabled: l.overrideDisabled,
            })),
          });

          setApiUpdating(false);
        } catch (e) {
          setApiUpdating(false);
          setApiError(e.message);
        }
      }

      setActiveLayout(null);
    },
    [eventId, round, slides],
  );

  const onLayoutUpdate = useCallback(
    async (
      layoutId: string,
      data: API.ReportingTemplateLayoutUpdateRequest,
    ) => {
      try {
        setApiUpdating(true);
        setSlides((list) => {
          const result = Array.from(list);
          const index = result.findIndex((r) => r.id === layoutId);
          result[index] = { ...result[index], ...data };
          return result;
        });
        await modelApi.updateReportingTemplate(eventId, round, {
          layouts: slides.map((l) => ({
            id: l.id,
            overrideDisabled:
              l.id === layoutId ? data.overrideDisabled : l.overrideDisabled,
          })),
        });

        setApiUpdating(false);
      } catch (e) {
        setApiUpdating(false);
        setApiError(e.message);
      }
    },
    [eventId, round, slides],
  );

  const sensors = useSensors(
    // useSensor(PointerSensor, { activationConstraint: { delay: 50, tolerance: 1, distance: 1 } })
    useSensor(PointerSensor),
  );

  return (
    <>
      {apiError != null && (
        <Banner type="error" active={!!apiError} message={apiError} />
      )}
      {mode === "thumbnails" && !editable && (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={slides}>
            <div
              className={classNames("reporting-template-select-container", {
                scrollable: true,
              })}
            >
              {slides.map((layout, layoutIndex) => (
                <SortableReportingPagePreviewTile
                  key={layout.id + layout.overrideDisabled}
                  id={layout.id}
                  data={{
                    ...data,
                    layouts: [layout],
                  }}
                  roundNumber={round}
                  layout={layout}
                  showHeader
                  showTeamPositions
                  overlay={
                    <Badge
                      value={layoutIndex + 1}
                      colour="green"
                      size={24}
                      textSize="5xl"
                      floating
                    />
                  }
                  onLayoutUpdate={onLayoutUpdate}
                  disabled={apiUpdating}
                  useOverrideDisabled
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {/* {activeLayout && <div id={activeLayout.id}>{activeLayout.id}</div>} */}
            {activeLayout ? (
              <ReportingPagePreviewTile
                key={activeLayout.id}
                id={activeLayout.id}
                data={{
                  ...data,
                  layouts: [activeLayout],
                }}
                roundNumber={round}
                layout={activeLayout}
                showHeader
                showTeamPositions
                // overlay={<Badge value={layoutIndex + 1} colour='green' size={24} textSize='5xl' floating/>}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      {mode === "fullscreen" && availableHeight > 0 && availableWidth > 0 && (
        <div className="reporting-template-select-container">
          <VerticalGroup
            style={{ flex: 1, height: availableHeight, width: availableWidth }}
            spread
            center
          >
            {previewHeight > 0 && previewWidth > 0 && (
              <ContentStretcher
                style={{
                  background: "#e1e1e1",
                  width: previewWidth,
                  height: previewHeight,
                  minHeight: previewHeight,
                }}
                contentWidth={RENDERED_DIMENSIONS.width}
                contentHeight={RENDERED_DIMENSIONS.height}
              >
                <div
                  style={{
                    width: RENDERED_DIMENSIONS.width,
                    height: RENDERED_DIMENSIONS.height,
                    minHeight: RENDERED_DIMENSIONS.height,
                    position: "absolute",
                  }}
                >
                  <RoundResultsPage
                    data={{
                      ...data,
                      layouts: slides,
                    }}
                    roundNumber={round}
                    onlyThesePages={[]}
                    startingLayout={slides[0].type}
                  />
                </div>
              </ContentStretcher>
            )}
          </VerticalGroup>
        </div>
      )}
    </>
  );
};

export default memo(SortableReportingTemplatePreviewContent);
